import type { JSX, ReactElement } from 'react';
import { cloneElement, Children } from 'react';
import { styled } from 'styled-components';
import { isNonEmptyArray } from '@wistia/type-guards';
import type { GradientNameType } from '../../private/helpers/getBackgroundGradient';
import { Thumbnail } from '../Thumbnail';
import type { ThumbnailProps } from '../Thumbnail';

const ThumbnailCollageContainer = styled.div`
  container-type: size;
  width: 100%;
  aspect-ratio: 16 / 9;
  display: flex;
`;

const StyledThumbnailCollage = styled.div<{ $gradientBackground: GradientNameType }>`
  display: grid;
  gap: var(--wui-space-01);
  width: 100%;
  grid-template-columns: 3fr 2fr;
  grid-template-rows: 1fr 1fr;

  &:has(:nth-child(1)) {
    grid-template-areas:
      'a a'
      'a a';
  }

  &:has(:nth-child(2)) {
    grid-template-areas:
      'a b'
      'a b';
  }

  &:has(:nth-child(3)) {
    grid-template-areas:
      'a b'
      'a c';
  }

  > :nth-child(1) {
    grid-area: a;
  }

  > :nth-child(2) {
    grid-area: b;
  }

  > :nth-child(3) {
    grid-area: c;
  }

  [data-wui-thumbnail-container] {
    container-type: unset;
    aspect-ratio: unset;
    height: 100%;
    width: 100%;
  }
`;

export type ThumbnailCollageProps = {
  /**
   * An array of `Thumbnail` components to show in the collage. Supports up to 3 `Thumbnail`s.
   */
  children?: ReactElement<ThumbnailProps>[] | undefined;
  /**
   * The type of gradient to show if no `Thumbnail`s are provided
   */
  gradientBackground?: GradientNameType;
};

/**
 * A `ThumbnailCollage` is a component that displays multiple `Thumbnail` components in a responsive grid layout.
 * It supports up to 3 thumbnails arranged in different configurations based on the number of children provided.
 * If no thumbnails are provided, it displays a fallback thumbnail with a gradient background.
 */
const ThumbnailCollage = ({
  children = [],
  gradientBackground = 'defaultMidOne',
  ...props
}: ThumbnailCollageProps): JSX.Element => {
  const thumbnailArray = Children.toArray(children);

  // limit children to a maximum of three thumbnails since that is the most we can display in the collage
  const truncatedThumbnails = thumbnailArray.slice(0, 3) as ReactElement<ThumbnailProps>[];

  const thumbnails = isNonEmptyArray(thumbnailArray)
    ? truncatedThumbnails.map((child) => {
        // Now that we know the child is a Thumbnail component, we can safely clone it
        return cloneElement(child, {
          ...child.props,
          children: undefined,
        });
      })
    : [
        // ThumbnailCollage will fallback to a Thumbnail with a gradient background if no children are provided
        <Thumbnail
          key="fallback"
          gradientBackground={gradientBackground}
          thumbnailUrl={null}
        />,
      ];

  return (
    <ThumbnailCollageContainer>
      <StyledThumbnailCollage
        $gradientBackground={gradientBackground}
        data-wui-thumbnail-collage={true}
        {...props}
      >
        {thumbnails}
      </StyledThumbnailCollage>
    </ThumbnailCollageContainer>
  );
};

export { ThumbnailCollage };
